import { useState, useEffect } from 'react';
import { useGameStore, useAuthHeaders, EventBus } from '@campus/core';
import { AvatarEditor } from '../avatar/AvatarEditor.js';
import { AvatarSaveError, draftFrom, saveAvatar } from '../avatar/pcAppearance.js';
import { API_BASE } from '../../lib/apiBase.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { Modal, Button, Input } from '../ds/index.js';
import { PhotoField } from '../people/PhotoField.js';
import { loadMembers, sessionInfo } from '../people/membersStore.js';

/**
 * ProfileModal — « Profil et avatar » : nom affiché + avatar dans une seule
 * fenêtre. PATCH /auth/me et /auth/me/appearance (seulement ce qui a changé),
 * puis diffusion via Colyseus (ui:profile-update, ui:appearance-update).
 */
export function ProfileModal({ onClose }: { onClose: () => void }) {
  const currentName = useGameStore((s) => s.displayName) ?? '';
  const currentAppearance = useGameStore((s) => s.appearance);
  const tenantId = useGameStore((s) => s.currentZoneId)?.split(':')[0] ?? '';
  const authHeaders = useAuthHeaders(tenantId);
  const isMobile = useIsMobile();

  const [name, setName] = useState(currentName);
  const [initialDraft] = useState(() => draftFrom(currentAppearance));
  const [draft, setDraft] = useState(initialDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { userId, isGuest } = sessionInfo();
  useEffect(() => { void loadMembers(0); }, []);

  useEffect(() => {
    EventBus.emit('ui:modal-opened', {});
    return () => { EventBus.emit('ui:modal-closed', {}); };
  }, []);

  const trimmed = name.trim();
  const nameChanged = trimmed !== currentName;
  // Un ancien avatar (LimeZu) compte comme modifié : l'enregistrer le passe au nouveau format
  const appearanceChanged = JSON.stringify(draft) !== JSON.stringify(initialDraft)
    || !(currentAppearance as { pc?: unknown; campus?: unknown } | null)?.pc
      && !(currentAppearance as { campus?: unknown } | null)?.campus;

  const handleSave = async () => {
    if (!trimmed || trimmed.length > 30) return;
    setSaving(true);
    const headers = { ...authHeaders, 'Content-Type': 'application/json' };

    if (nameChanged) {
      try {
        await fetch(`${API_BASE}/auth/me`, { method: 'PATCH', headers, body: JSON.stringify({ displayName: trimmed }) });
      } catch { /* non-critique — le broadcast Colyseus suffit */ }
      const avatarIndex = useGameStore.getState().avatarIndex;
      EventBus.emit('ui:profile-update', { displayName: trimmed, avatarIndex });
    }

    if (appearanceChanged) {
      try {
        const appearance = await saveAvatar(draft, authHeaders);
        useGameStore.getState().setAppearance(appearance);
        EventBus.emit('ui:appearance-update', { appearance });
      } catch (err) {
        setError(err instanceof AvatarSaveError ? err.message : 'Impossible d\'enregistrer ton avatar. Réessaie.');
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    onClose();
  };

  return (
    <Modal
      title="Profil et avatar"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant={isMobile ? 'secondary' : 'ghost'} onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={() => { void handleSave(); }} disabled={!trimmed || saving || (!nameChanged && !appearanceChanged)}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      {error && <p role="alert" style={{ margin: '0 0 12px', fontSize: 'var(--text-sm)', color: 'var(--color-danger)' }}>{error}</p>}
      <AvatarEditor
        value={draft}
        onChange={setDraft}
        headers={authHeaders}
        compact={isMobile}
        aside={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input
            label="Nom affiché"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={30}
            {...(isMobile ? {} : { hint: 'Visible au-dessus de ton avatar.' })}
            {...(!trimmed ? { error: 'Écris un nom.' } : {})}
          />
          {!isGuest && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="pl-elabel" style={{ margin: 0 }}>Photo de profil</span>
              <PhotoField userId={userId} name={trimmed || currentName} endpoint="/auth/me/photo" />
              <span className="pl-help">Affichée dans la messagerie et la liste des personnes. Sans photo : le visage de ton avatar.</span>
            </div>
          )}
          </div>
        }
      />
    </Modal>
  );
}
